/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useMemo, useState } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Bar } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// HorizontalBarChart configurations
import configs from "examples/Charts/BarCharts/HorizontalBarChart/configs";

// Soft UI Dashboard PRO React base styles
import { Button, Grid, Icon } from "@mui/material";
import DataTable from "Tables/DataTable";

function HorizontalBarChart({ title, description, height, chart, informParent }) {
  const chartDatasets = chart.datasets
    ? chart.datasets.map((dataset) => ({
        ...dataset,
        weight: 5,
        borderRadius: 4,
        // backgroundColor: colors[dataset.color]
        //   ? colors[dataset.color || "dark"].main
        //   : colors.dark.main,
        backgroundColor: [
          "rgba(255, 99, 132, 0.2)",
          "rgba(255, 159, 64, 0.2)",
          "rgba(255, 205, 86, 0.2)",
          "rgba(75, 192, 192, 0.2)",
          "rgba(54, 162, 235, 0.2)",
          "rgba(153, 102, 255, 0.2)",
          "rgba(201, 203, 207, 0.2)",
        ],
        borderColor: [
          "rgb(255, 99, 132)",
          "rgb(255, 159, 64)",
          "rgb(255, 205, 86)",
          "rgb(75, 192, 192)",
          "rgb(54, 162, 235)",
          "rgb(153, 102, 255)",
          "rgb(201, 203, 207)",
        ],
        borderWidth: 2,
        fill: false,
        maxBarThickness: 35,
        minBarLength: 5,
      }))
    : [];

  const { data, options } = configs(chart.labels || [], chartDatasets);
  const [chartView, setChartView] = useState(true);

  const [dataTableData, setDataTableData] = useState({
    columns: [
      { Header: "Platform Id", accessor: "platform_id", width: "100px" },
      { Header: "Platform Name", accessor: "platform_name", width: "100px" },
      { Header: "Bets", accessor: "total_bets", width: "120px" },
    ],

    rows: [],
  });

  const changeView = (e) => {
    let currentView = chartView;
    setChartView(currentView ? false : true);
    informParent(e);
  };

  useEffect(() => {
    if (chart.datasets && chart.datasets[0] && chart.datasets[0].tableData) {
      let dataTable = dataTableData;
      (dataTable.columns =
        chart.datasets[0].label == "Total Bets"
          ? [
              { Header: "Platform Id", accessor: "platform_id", width: "100px" },
              { Header: "Platform Name", accessor: "platform_name", width: "100px" },
              { Header: "Bets", accessor: "total_bets", width: "120px" },
            ]
          : [
              { Header: "Platform Id", accessor: "platform_id", width: "100px" },
              { Header: "Platform Name", accessor: "platform_name", width: "100px" },
              { Header: "Bets", accessor: "total_bet_odds", width: "120px" },
            ]),
        (dataTable.rows = chart.datasets[0].tableData);
      setDataTableData(dataTable);
    }
  }, [chart.datasets]);

  const renderChart = (
    <SoftBox p={2}>
      {title || description ? (
        <SoftBox px={description ? 1 : 0} pt={description ? 1 : 0}>
          {title && (
            <SoftBox mb={1}>
              <Grid container>
                <Grid item sm={10}>
                  <SoftTypography variant="h6">{title}</SoftTypography>
                </Grid>
                <Grid item sm={2} style={{ textAlign: "right" }}>
                  <Grid container>
                    <Grid item sm={12} style={{ textAlign: "right" }}>
                      <SoftBox>
                        <Button
                          onClick={changeView}
                          style={{
                            margin: "0px",
                            padding: "0px",
                            width: "32px",
                            maxWidth: "32px",
                            minWidth: "16px",
                          }}
                        >
                          <Icon>
                            {chartView
                              ? "format_list_bulleted_outlined_icon"
                              : "data_usage_outlined_icon"}
                          </Icon>
                        </Button>
                      </SoftBox>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </SoftBox>
          )}
          <SoftBox mb={2}>
            <SoftTypography component="div" variant="button" fontWeight="regular" color="text">
              {description}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      ) : null}
      {useMemo(
        () => (
          <SoftBox
            className={"bar-container-" + chart.labels.length}
            height={chart.labels.length * 2.1 + "em"}
            minHeight={"4.8em"}
          >
            <Bar data={data} options={options} barlabel="value" />
          </SoftBox>
        ),
        [chart, height]
      )}
    </SoftBox>
  );

  const renderTable = (
    <SoftBox p={2}>
      {title || description ? (
        <SoftBox px={description ? 1 : 0} pt={description ? 1 : 0}>
          {title && (
            <SoftBox mb={1}>
              <Grid container>
                <Grid item sm={10}>
                  <SoftTypography variant="h6">{title}</SoftTypography>
                </Grid>
                <Grid item sm={2} style={{ textAlign: "right" }}>
                  <Grid container>
                    <Grid item sm={12} style={{ textAlign: "right" }}>
                      <SoftBox>
                        <Button
                          onClick={changeView}
                          style={{
                            margin: "0px",
                            padding: "0px",
                            width: "32px",
                            maxWidth: "32px",
                            minWidth: "16px",
                          }}
                        >
                          <Icon>
                            {chartView
                              ? "format_list_bulleted_outlined_icon"
                              : "data_usage_outlined_icon"}
                          </Icon>
                        </Button>
                      </SoftBox>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </SoftBox>
          )}
          <SoftBox mb={2}>
            <SoftTypography component="div" variant="button" fontWeight="regular" color="text">
              {description}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      ) : null}
      <SoftBox className={"bar-container-" + chart.labels.length} height="100%" minHeight={"4.8em"}>
        <DataTable
          table={dataTableData}
          entriesPerPage={{
            defaultValue: 10,
            entries: [5, 10, 15, 20, 25],
          }}
          hiddenColumns={[]}
          refreshEnabled={false}
          // openBackDrop={loading}
          // informParent={handleInformParent}
          canSearch={false}
        />
      </SoftBox>
    </SoftBox>
  );

  return chartView ? (
    title || description ? (
      <Card>{renderChart}</Card>
    ) : (
      renderChart
    )
  ) : (
    <Card>{renderTable}</Card>
  );
}

// Setting default values for the props of HorizontalBarChart
HorizontalBarChart.defaultProps = {
  title: "",
  description: "",
  height: "19.125rem",
};

// Typechecking props for the HorizontalBarChart
HorizontalBarChart.propTypes = {
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.array).isRequired,
  informParent: PropTypes.func.isRequired,
};

export default HorizontalBarChart;
